/**
 * I FORMATI DELLE QUOTE.
 *
 * Il valore vero e' UNO SOLO: il DECIMALE, quello che sta nel catalogo e nel
 * ticket. Tutto il resto - frazionario, americano, Hong Kong, malese,
 * indonesiano - e' solo un modo di scriverlo, e si calcola qui al momento
 * di MOSTRARE. I conti (vincita, bonus, colonne dei sistemi) si fanno sempre
 * in decimale e si convertono alla fine: convertire ogni quota e poi fare i
 * conti darebbe numeri sbagliati.
 *
 * COSA FA: dato un decimale e un formato, torna la stringa da scrivere.
 * COSA NON FA: non converte mai all'indietro per fare conti, e non decide
 * quale formato usare - quello e' una preferenza di chi guarda (preferenze.js).
 *
 * Le due trappole, scritte qui perche' sono quelle che fanno sbagliare:
 *  - MALESE e INDONESIANO hanno il segno rovesciato l'uno rispetto all'altro:
 *    il malese va in negativo SOPRA 2.00, l'indonesiano SOTTO 2.00.
 *  - Il FRAZIONARIO NON SI ARROTONDA alla frazione piu' vicina. I bookmaker
 *    inglesi hanno una scala di prezzi fatta di frazioni (10/11, 5/6, 4/5) e
 *    la loro quota vera E' la frazione. Qui la quota vera e' il decimale, a
 *    un centesimo qualunque: cercare nella scala la frazione piu' vicina
 *    scriveva 1.75 come 4/5, che vale 1.80 -- piu' di quello che si paga.
 *    La frazione si calcola sul decimale: la regola sta sopra frazionario().
 */
import i18next from 'i18next';

export const FORMATI = [
  {chiave: 'decimale',    get nome() { return i18next.t('Decimale'); },        esempio: '1.85'},
  {chiave: 'frazionario', get nome() { return i18next.t('Frazionario (UK)'); }, esempio: '17/20'},
  {chiave: 'americano',   get nome() { return i18next.t('Americano'); },        esempio: '-118'},
  {chiave: 'hongkong',    get nome() { return i18next.t('Hong Kong'); },        esempio: '0.85'},
  {chiave: 'malese',      get nome() { return i18next.t('Malese'); },           esempio: '0.85'},
  {chiave: 'indonesiano', get nome() { return i18next.t('Indonesiano'); },      esempio: '-1.18'},
];

export const FORMATO_PREDEFINITO = 'decimale';

/* LE FRAZIONI STORICHE della scala inglese che non cadono su un centesimo.
   La chiave sono i centesimi di (d - 1): 91 e' la quota 1.91.

   Ci sta SOLO chi vale la quota a due cifre SENZA SUPERARLA: 10/11 fa 1.9091,
   che a due cifre e' 1.91 e non lo supera. Le altre storiche restano fuori
   apposta: 5/6 fa 1.8333, piu' di 1.83, e scriverla vorrebbe dire promettere
   0,33 in piu' ogni 100 giocati rispetto a quello che si paga. Lo stesso per
   1/3, 4/9, 4/7, 8/15, 4/11, 2/9, 10/3: quelle quote si scrivono esatte.

   Non si aggiunge a occhio: il test rifa' il controllo su ogni quota. */
const STORICHE = {
  13: '1/8', 17: '1/6', 29: '2/7', 62: '8/13', 67: '4/6', 73: '8/11', 91: '10/11',
  138: '11/8', 163: '13/8', 188: '15/8',
};

/* Quello che in Inghilterra non si semplifica: uno e mezzo a uno e' 6/4,
   mai 3/2. */
const COME_SI_SCRIVE = {'3/2': '6/4'};

function numero(d) {
  const n = Number(d);
  return Number.isFinite(n) && n > 1 ? n : null;
}

/* Due decimali, HALF_UP come sul server (toFixed e' quasi sempre uguale ma
   non su alcuni binari: si passa per il centesimo intero). */
function due(n) {
  /* HALF_UP lontano dallo zero, con un pelo di tolleranza: 1.005 in binario
     e' 1.00499..., e senza tolleranza verrebbe 1.00. */
  const segno = n < 0 ? -1 : 1;
  const cent = Math.round(Math.abs(n) * 100 + 1e-7);
  return ((segno * cent) / 100).toFixed(2);
}

function conSegno(n) {
  return (n > 0 ? '+' : '') + n;
}

function massimoComunDivisore(a, b) {
  let x = a;
  let y = b;
  while (y) {
    const resto = x % y;
    x = y;
    y = resto;
  }
  return x;
}

export function decimale(d) {
  const n = numero(d);
  return n === null ? '' : due(n);
}

/**
 * FRAZIONARIO: quanto si VINCE per quanto si GIOCA. 17/20 = con 20 se ne
 * vincono 17.
 *
 * LA REGOLA: la frazione vale la quota vera, MAI DI PIU'. La quota vera e' il
 * decimale: e' su quello che si paga, qualunque formato si legga. Una
 * frazione piu' alta del decimale e' una promessa che la cassa non mantiene.
 *
 * COME SI SCRIVE, nell'ordine:
 *  1. si parte dai centesimi di (d - 1), gli stessi che scrive il decimale;
 *  2. le dieci frazioni storiche che valgono quella quota senza superarla
 *     (10/11 per 1.91, 8/11 per 1.73, 11/8 per 2.38) si scrivono cosi';
 *  3. sotto la pari la frazione esatta. Semplificata quando il denominatore
 *     e' da lavagna (fino a 20: 1.85 -> 17/20, 1.75 -> 3/4) o quando e' uno
 *     a tanti (1.02 -> 1/50); se no resta SUI CENTO, che si legge da sola:
 *     1.83 -> 83/100, con 100 se ne vincono 83 (18/25 per 1.72 e' la stessa
 *     cosa, ma va fatta a mente);
 *  4. dalla pari in su la frazione esatta SE E' CORTA -- denominatore fino a
 *     20, numeratore fino a due cifre, e sopra 10 a 1 solo numeri interi:
 *     2.25 -> 5/4, 2.05 -> 21/20, 26.00 -> 25/1;
 *  5. altrimenti TANTO A UNO: 2.47 -> 1.47/1, 6.22 -> 5.22/1,
 *     153.27 -> 152.27/1. E' la formula dei bookmaker inglesi -- la quota
 *     meno uno, sopra uno -- lasciata com'e' quando non si semplifica in
 *     niente di leggibile: 147/100 e 15227/100 sono esatte uguale, ma non si
 *     leggono.
 *
 * COSA NON FA: non cerca in una scala la frazione "piu' vicina". Lo faceva,
 * e su 10.000 quote ne scriveva 4.964 piu' alte del vero (1.75 -> 4/5, che
 * vale 1.80; 59.01 -> 66/1) usando 73 frazioni in tutto: centinaia di quote
 * diverse finivano scritte uguali.
 */
export function frazionario(d) {
  const n = numero(d);
  if (n === null) return '';

  /* I centesimi netti, con lo stesso arrotondamento di due(): la frazione
     dice lo stesso numero che dice il decimale, non uno accanto. */
  const centesimi = Math.round(n * 100 + 1e-7) - 100;
  if (centesimi <= 0) return '0/1';

  if (STORICHE[centesimi]) return STORICHE[centesimi];

  const divisore = massimoComunDivisore(centesimi, 100);
  const sopra = centesimi / divisore;
  const sotto = 100 / divisore;

  if (centesimi < 100) {
    return (sotto <= 20 || sopra === 1) ? `${sopra}/${sotto}` : `${centesimi}/100`;
  }

  const corta = sotto <= 20 && sopra <= 99 && (sotto === 1 || centesimi < 1000);
  if (corta) {
    const scritta = `${sopra}/${sotto}`;
    return COME_SI_SCRIVE[scritta] || scritta;
  }

  return `${centesimi / 100}/1`;
}

/* Americano: pari (2.00) e sopra positivo, sotto negativo. Sempre intero. */
export function americano(d) {
  const n = numero(d);
  if (n === null) return '';
  if (n >= 2) return conSegno(Math.round((n - 1) * 100));
  return String(Math.round(-100 / (n - 1)));
}

export function hongkong(d) {
  const n = numero(d);
  return n === null ? '' : due(n - 1);
}

/* Malese: fino a 2.00 compreso e' d-1 (positivo, al massimo 1.00); SOPRA 2.00
   diventa negativo. */
export function malese(d) {
  const n = numero(d);
  if (n === null) return '';
  if (n <= 2) return due(n - 1);
  return due(-1 / (n - 1));
}

/* Indonesiano: da 2.00 in su e' d-1 (positivo); SOTTO 2.00 diventa negativo. */
export function indonesiano(d) {
  const n = numero(d);
  if (n === null) return '';
  if (n >= 2) return due(n - 1);
  return due(-1 / (n - 1));
}

const PER_CHIAVE = {decimale, frazionario, americano, hongkong, malese, indonesiano};

/**
 * La quota scritta nel formato chiesto. Formato ignoto o mancante = decimale,
 * e non e' un ripiego inventato: il decimale e' il valore vero.
 */
export function scriviQuota(d, formato) {
  const f = PER_CHIAVE[formato] || decimale;
  return f(d);
}

/** Il formato esiste? Serve a non salvare una preferenza fuori elenco. */
export function formatoValido(chiave) {
  return !!PER_CHIAVE[chiave];
}
